package com.example.mystybag.common.views

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.systemBars
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p05
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p28
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.extensions.ShimmerListColumn
import com.example.mystybag.common.extensions.doOnClick
import com.example.mystybag.common.extensions.keyboardPadding
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.extensions.roundedContainer
import com.example.mystybag.common.extensions.shimmer
import com.example.mystybag.common.logic.ChooseCountryViewModel
import com.example.mystybag.common.model.CountryVisualModel
import com.example.mystybag.common.ui.components.custom.NoSearchResultLayout
import com.example.mystybag.common.ui.components.custom.SearchTextField
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView

@Composable
fun ChooseCountryView(viewModel: ChooseCountryViewModel) {
    val insetsBottomPadding = WindowInsets.systemBars.asPaddingValues().calculateBottomPadding()
    val countries           = viewModel.uiData.countries.collectAsState()
    val selectedCountries   = viewModel.uiData.selectedCountries.collectAsState()
    val showNoResult        = viewModel.uiData.showNoResult.collectAsState()
    val showDoneButton      = viewModel.uiData.showDoneButton.collectAsState()
    val isLoading           = viewModel.uiData.isLoading.collectAsState()

    @Composable
    fun GetSelectedCountryView(country: CountryVisualModel) {
        Row(
            modifier          = Modifier.padding(vertical = p4).rippleClick(action = remember(country.isoAlpha3) { {
                viewModel.onCountryClicked(country)
            } }),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                modifier            = Modifier.size(p16),
                painter             = painterResource(SRC.images.navigation_close),
                tint                = AppTheme.current.colors.onSurface,
                contentDescription  = "",
            )
            Texts.BodySmall(
                text        = country.name,
                color       = AppTheme.current.colors.onSurface,
                modifier    = Modifier
                    .padding(horizontal = p6)
                    .doOnClick(remember(country.isoAlpha3) { {
                        viewModel.onCountryClicked(country)
                    } })
            )
        }
    }

    UIView(
        modifier                = Modifier.padding(horizontal = p16),
        viewModel               = viewModel,
        floatingActionButton    = {
            AnimatedVisibility(
                visible = showDoneButton.value,
                enter   = fadeIn(animationSpec = tween(100, easing = LinearEasing)),
            ) {
                FloatingActionButton(
                    modifier        = Modifier.padding(bottom = insetsBottomPadding).keyboardPadding(),
                    containerColor  = AppTheme.current.colors.inversePrimary,
                    onClick         = remember { viewModel::onDoneButtonClicked }
                ) {
                    Icon(
                        modifier            = Modifier.size(p16),
                        painter             = painterResource(SRC.images.check_menu_dark),
                        tint                = AppTheme.current.colors.onPrimary,
                        contentDescription  = "",
                    )
                }
            }
        }
    ) {
        if (selectedCountries.value.isNotEmpty()) {
            val scrollStateHorizontal = rememberScrollState()
            Row(
                modifier = Modifier
                    .padding(bottom = p16)
                    .fillMaxWidth()
                    .roundedContainer()
                    .horizontalScroll(scrollStateHorizontal),
                horizontalArrangement = Arrangement.spacedBy(p10)
            ) {
                List(selectedCountries.value.size) { country ->
                    GetSelectedCountryView(country = selectedCountries.value[country])
                }
            }
        }

        SearchTextField(
            modifier            = Modifier.fillMaxWidth().padding(bottom = LocalTheme.p16),
            searchCallbacks     = viewModel.searchControls,
            maxLines            = 1
        )

        if (isLoading.value || countries.value.isNotEmpty())
            ShimmerListColumn(
                shimmerItem = { isLoading -> chooseCountryShimmer(isLoading) },
                list        = countries.value,
                isLoading   = isLoading.value,
                key         = { it.isoAlpha3 },
                onClick     = remember { viewModel::onCountryClicked }
            )

        if (showNoResult.value)
            NoSearchResultLayout()
    }

}

fun LazyListScope.chooseCountryShimmer(isLoading: Boolean) {
    item {
        repeat(3) {
            Row(
                verticalAlignment   = Alignment.CenterVertically,
                modifier            = Modifier
                    .fillMaxWidth()
                    .background(AppTheme.current.colors.surface)
                    .padding(top = p16),
            ) {
                Column {
                    Row (
                        modifier            = Modifier.padding(bottom = p16),
                        verticalAlignment   = Alignment.CenterVertically
                    ) {
                        Box(modifier = Modifier
                            .size(p28)
                            .clip(CircleShape)
                            .shimmer(isLoading, isCircleShape = true, width = .2f)
                        )
                        Texts.TitleLarge(
                            modifier    = Modifier
                                .padding(start = p16)
                                .shimmer(isLoading, width = .9f),
                            text        = "text"
                        )
                    }
                    HorizontalDivider(
                        modifier    = Modifier
                            .padding(start = p48)
                            .fillMaxWidth(),
                        thickness   = p05,
                        color       = AppTheme.current.colors.surface
                    )
                }
            }
        }
    }
}
